import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@peacecover/icon';
import { useGetAdaptedFictionPreviews } from '../../../domains/scene/ui/hooks/useGetAdaptedFictionPreviews';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import type { ScenePersona } from '../types';

const ANIMATION_DELAY_MS = 3 * 1000;

interface LoadingSceneStepProps {
  firstPersona: ScenePersona | null;
  secondPersona: ScenePersona | null;
}

const LoadingSceneStep = ({ firstPersona, secondPersona }: LoadingSceneStepProps) => {
  const navigate = useNavigate();
  const castRelationshipId =
    localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_CAST_RELATIONSHIP_ID) ?? '';

  // 씬 메인에서 사용할 데이터를 미리 한 번 호출해 캐시 워밍업
  useGetAdaptedFictionPreviews({
    perPage: 10,
    castRelationshipId,
    enabled: Boolean(castRelationshipId)
  });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      navigate('/scene', { replace: true });
    }, ANIMATION_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [navigate]);

  return (
    <div
      className="min-h-screen overflow-hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      style={{
        backgroundImage: 'url(/assets/scene/bg_scene_loading.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative'
      }}
    >
      <header className="px-[16px] py-[15px]">
        <ArrowLeft size={24} className="text-pure-white" />
      </header>
      <div className="px-[24px] py-[10px] flex flex-col gap-[14px]">
        <h1 className="text-[26px] font-bold leading-[31px] text-pure-white">
          입력한 등장인물과 상황으로
          <br />
          나만의 씬을 만들어볼게요
        </h1>
        <p className="text-[15px] font-medium leading-[18px] text-main-dark-4">
          잠시만 기다려주세요...
        </p>
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 top-[35%]">
        <div className="relative">
          {firstPersona?.dataUrl && (
            <img
              src={firstPersona.dataUrl}
              alt="first persona"
              className="absolute top-[49px] right-[63px] w-[130px] h-[130px] object-cover rounded-full"
            />
          )}
          {secondPersona?.dataUrl && (
            <img
              src={secondPersona.dataUrl}
              alt="second persona"
              className="absolute bottom-[61px] left-[76px] w-[130px] h-[130px] object-cover rounded-full"
            />
          )}
          <div
            className="w-[360px] h-[360px] opacity-[0.46] rounded-full bg-[linear-gradient(219deg,rgba(18,18,34,0.60)_14.31%,rgba(0,0,0,0.00)_49.56%,rgba(105,106,130,0.60)_84.81%),linear-gradient(136deg,rgba(255,222,245,0.50)_-4.95%,rgba(202,221,255,0.50)_70.27%,rgba(211,238,255,0.50)_105.23%)]"
            style={{
              filter: 'blur(5px)'
            }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <GradientLayer />
          </div>
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[319px] h-[319px] rounded-full overflow-hidden"
            style={{
              boxShadow:
                '-7px -9px 12.5px 0 rgba(255, 255, 255, 0.42) inset, 3px 3px 4px 0 rgba(255, 255, 255, 0.47) inset, 6px 0 34.6px 10px rgba(255, 255, 255, 0.56) inset, -71px -65px 10.4px 0 rgba(135, 123, 244, 0.06) inset'
            }}
          >
            <div
              className="absolute inset-0 rounded-full bg-[linear-gradient(136deg,rgba(70,212,178,0.50)_-4.95%,rgba(86,107,229,0.50)_70.27%,rgba(93,62,250,0.50)_105.23%)] animate-spin"
              style={{
                animationDuration: '2s',
                animationTimingFunction: 'linear'
              }}
            />
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] flex items-center justify-center">
            <div className="w-[360px] h-[360px] scale-125">
              <img
                src="/assets/scene/planetary_ring.png"
                alt="planetary ring"
                className="w-full h-full animate-spin origin-center"
                style={{
                  animationDuration: '10s',
                  animationTimingFunction: 'linear'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingSceneStep;

const GradientLayer = () => {
  return (
    <div className="relative w-[268px] h-[234px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="267"
        height="306"
        viewBox="0 0 267 306"
        fill="none"
        aria-label="test1"
        role="img"
        className="absolute top-0 left-0 w-[195px] h-[234px]"
      >
        <g filter="url(#filter0_if_444_7243)">
          <ellipse cx="133.2" cy="152.7" rx="97.5" ry="117" fill="url(#paint0_linear_444_7243)" />
        </g>
        <defs>
          <filter
            id="filter0_if_444_7243"
            x="0.00019455"
            y="-4.95911e-05"
            width="266.4"
            height="305.4"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dx="33" dy="-2" />
            <feGaussianBlur stdDeviation="2.65" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.593905 0 0 0 0 0.818945 0 0 0 0 0.958834 0 0 0 0.76 0"
            />
            <feBlend mode="normal" in2="shape" result="effect1_innerShadow_444_7243" />
            <feGaussianBlur stdDeviation="17.85" result="effect2_foregroundBlur_444_7243" />
          </filter>
          <linearGradient
            id="paint0_linear_444_7243"
            x1="43.4926"
            y1="70.5947"
            x2="248.114"
            y2="177.342"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#66FFCB" stop-opacity="0.5" />
            <stop offset="0.682692" stop-color="#30217C" stop-opacity="0.4" />
            <stop offset="1" stop-color="#606EFF" stop-opacity="0.05" />
          </linearGradient>
        </defs>
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="236"
        height="261"
        viewBox="0 0 236 261"
        fill="none"
        aria-label="test2"
        role="img"
        className="absolute bottom-0 right-0 w-[195px] h-[220px]"
      >
        <g filter="url(#filter0_f_444_7244)">
          <ellipse cx="117.8" cy="130.3" rx="97.5" ry="110" fill="url(#paint0_linear_444_7244)" />
        </g>
        <defs>
          <filter
            id="filter0_f_444_7244"
            x="4.95911e-05"
            y="4.95911e-05"
            width="235.6"
            height="260.6"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="10.15" result="effect1_foregroundBlur_444_7244" />
          </filter>
          <linearGradient
            id="paint0_linear_444_7244"
            x1="28.0924"
            y1="53.1071"
            x2="227.122"
            y2="163.544"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.317308" stop-color="#46D5B2" stop-opacity="0" />
            <stop offset="1" stop-color="#5C3DFA" stop-opacity="0.1" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};
